import React, { useEffect, useState } from "react";
import { api, ApiError, type Subscription } from "../../api";
import { useToast } from "../../ToastContext";
import { useLanguage } from "../../i18n";
import { loadRazorpayCheckout } from "../../razorpayCheckout";
import { PlanChangedModal } from "../shared/PlanChangedModal";

function fmtDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export function TenantPlan({ onBack }: { onBack: () => void }) {
  const { t } = useLanguage();
  const toast = useToast();
  const [sub, setSub] = useState<Subscription | null>(null);
  const [busy, setBusy] = useState(false);
  const [razorpayKeyId, setRazorpayKeyId] = useState<string | null>(null);
  const [justPaid, setJustPaid] = useState(false);

  function load() {
    api.get("/api/subscription").then(setSub).catch(() => {});
  }
  useEffect(() => {
    load();
    api.get("/api/config").then(({ razorpayKeyId }) => setRazorpayKeyId(razorpayKeyId)).catch(() => {});
  }, []);

  async function waitForPro() {
    for (let i = 0; i < 6; i++) {
      const fresh: Subscription = await api.get("/api/subscription");
      if (fresh.plan === "pro") {
        setSub(fresh);
        setJustPaid(true);
        return;
      }
      await new Promise((r) => setTimeout(r, 1500));
    }
    load();
  }

  async function switchTo(plan: "pro" | "basic") {
    setBusy(true);
    try {
      if (plan === "basic" || !razorpayKeyId) {
        await api.post("/api/subscription", { plan });
        load();
        toast(t(plan === "pro" ? "tenantPlan.subscribedToast" : "tenantPlan.cancelledToast"));
        return;
      }
      await loadRazorpayCheckout();
      // Tenant Pro is a one-time payment for 30 days of access, not a
      // recurring mandate — a plain order-based checkout, same as a rent
      // payment, with the amount/currency Razorpay needs passed explicitly.
      const order = await api.post("/api/subscription/checkout", { plan: "pro", cycle: "monthly" });
      await new Promise<void>((resolve, reject) => {
        const rzp = new window.Razorpay({
          key: order.keyId,
          amount: order.amount,
          currency: order.currency,
          order_id: order.orderId,
          name: "ToLetBook",
          image: "https://toletbook.in/icons/icon-512.png",
          description: t("tenantPlan.proName"),
          handler: () => resolve(),
          modal: { ondismiss: () => reject(new Error("cancelled")) },
        });
        rzp.on("payment.failed", () => reject(new Error("failed")));
        rzp.open();
      });
      await waitForPro();
    } catch (err) {
      if (err instanceof Error && err.message === "failed") toast(t("tenantPlan.switchFailedToast"));
      else if (err instanceof ApiError) toast(err.message);
      // "cancelled" (user closed checkout) fails silently
    } finally {
      setBusy(false);
    }
  }

  const isPro = sub?.plan === "pro";

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <button className="icon-btn" onClick={onBack} aria-label="Back">‹</button>
        <div className="section-title" style={{ marginBottom: 0 }}>{t("tenantPlan.title")}</div>
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-row">
          <div>
            <div className="sub">{t("tenantPlan.currentPlanLabel")}</div>
            <b>{isPro ? t("tenantPlan.proName") : t("tenantPlan.basicName")}</b>
          </div>
        </div>
        {sub?.status === "trialing" && (
          <div className="sub" style={{ marginTop: 8, color: "var(--brand-700)", fontWeight: 700 }}>
            🎁 {t("tenantPlan.trialBadge")} · {t("tenantPlan.trialEndsOn", { date: fmtDate(sub.trialEndsAt) })}
          </div>
        )}
        {isPro && sub?.status === "active" && sub?.currentPeriodEnd && (
          <div className="sub" style={{ marginTop: 8 }}>{t("tenantPlan.activeUntil", { date: fmtDate(sub.currentPeriodEnd) })}</div>
        )}
        {isPro && (
          <button type="button" className="link-btn" style={{ marginTop: 8 }} disabled={busy} onClick={() => switchTo("basic")}>
            {t("tenantPlan.cancelPlanBtn")}
          </button>
        )}
      </div>

      <div className="plan-card accent">
        <div className="plan-head">
          <span className="plan-name">{t("tenantPlan.proName")}</span>
          <span className="plan-price">₹99<small>{t("tenantPlan.priceValidity")}</small></span>
        </div>
        <div className="plan-sub">{t("tenantPlan.proSub")}</div>
        <div className="plan-feat-list">
          <div className="plan-feat-row on"><span className="plan-feat-tick">✓</span> {t("tenantPlan.featUnlock")}</div>
          <div className="plan-feat-row on"><span className="plan-feat-tick">✓</span> {t("tenantPlan.featNearby")}</div>
          <div className="plan-feat-row on"><span className="plan-feat-tick">✓</span> {t("tenantPlan.featWhatsapp")}</div>
          <div className="plan-feat-row on"><span className="plan-feat-tick">✓</span> {t("tenantPlan.featSavedSearch")}</div>
          <div className="plan-feat-row off"><span className="plan-feat-tick">–</span> {t("tenantPlan.featFallback")}</div>
        </div>
        {isPro ? (
          <button className="btn-outline" disabled style={{ opacity: 0.6, cursor: "default" }}>{t("tenantPlan.currentPlanBtn")}</button>
        ) : (
          <button className="btn-solid" disabled={busy} onClick={() => switchTo("pro")}>{t("tenantPlan.subscribeBtn")}</button>
        )}
      </div>

      <div className="sub" style={{ textAlign: "center", marginTop: 4 }}>
        {razorpayKeyId ? t("tenantPlan.securePaymentNote") : t("tenantPlan.noChargeNote")}
      </div>

      {justPaid && <PlanChangedModal planName={t("tenantPlan.proName")} onClose={() => setJustPaid(false)} />}
    </>
  );
}
